/**
 * ContextResultView — Success screen for the Context Builder module.
 */
import { context } from '@/api/client'

interface ContextResultViewProps {
  jobId:    string
  meta?:    Record<string, unknown>
  onRerun:  () => void
  onReset:  () => void
}

function DownloadIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
      <path d="M6.5 2v7M3.5 7l3 3 3-3M2 11h9"
            stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )
}

function DocIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <rect x="2.5" y="1.5" width="9" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.2"/>
      <path d="M4.5 5h5M4.5 7.5h5M4.5 10h3" stroke="currentColor" strokeWidth="1.2"
            strokeLinecap="round"/>
    </svg>
  )
}

export default function ContextResultView({
  jobId, meta, onRerun, onReset,
}: ContextResultViewProps) {
  const docNames = meta?.doc_names as string[] | undefined
  const docCount = docNames?.length ?? (meta?.files as Record<string, unknown>)?.doc_count as number | undefined

  return (
    <div className="animate-fade-up">
      {/* Sticky action bar */}
      <div className="sticky top-14 z-30 glass-bar border-b border-slate-100 px-5 py-3">
        <div className="max-w-4xl mx-auto flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 mr-auto min-w-0">
            <span className="flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 text-emerald-700
                             rounded-full text-xs font-medium border border-emerald-100 flex-shrink-0">
              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
              Documents ready
            </span>
            {docCount !== undefined && (
              <span className="hidden sm:inline text-xs text-slate-400">
                · {docCount} document{docCount !== 1 ? 's' : ''} generated
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <a href={context.zipUrl(jobId)} download className="action-btn-ghost">
              <DownloadIcon /> Download ZIP
            </a>
            <button onClick={onReset} className="action-btn-ghost">
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
                <path d="M9 2l2 2-7 7H2V9l7-7z"
                      stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Edit
            </button>
            <button
              onClick={onRerun}
              className="flex items-center gap-1.5 px-3.5 py-1.5 bg-navy-900 text-white
                         rounded-lg text-xs font-medium hover:bg-navy-800 transition-colors
                         focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
            >
              <svg width="11" height="11" viewBox="0 0 11 11" fill="none" aria-hidden="true">
                <path d="M1.5 5.5A4 4 0 1 1 5.5 9.5M1.5 3v2.5H4"
                      stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Re-run
            </button>
          </div>
        </div>
      </div>

      {/* Result card */}
      <div className="max-w-4xl mx-auto px-5 pt-10 pb-16">
        <div className="bg-white rounded-2xl shadow-card border border-slate-100 p-10">
          <div className="text-center mb-8">
            <div className="mx-auto mb-6 w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center text-3xl">
              📁
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Context documents ready</h2>
            {docCount !== undefined && (
              <p className="text-slate-500 text-sm">
                {docCount} document{docCount !== 1 ? 's' : ''} generated and packaged.
              </p>
            )}
          </div>

          {/* Document list */}
          {docNames && docNames.length > 0 && (
            <div className="mb-8 bg-slate-50 rounded-xl p-4 divide-y divide-slate-100">
              {docNames.map(name => (
                <div key={name} className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0">
                  <span className="text-slate-400 flex-shrink-0"><DocIcon /></span>
                  <span className="text-sm text-slate-700 font-mono truncate">{name}</span>
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-center">
            <a
              href={context.zipUrl(jobId)}
              download
              className="flex items-center justify-center gap-2 px-7 py-3.5 bg-navy-900 text-white
                         rounded-xl text-sm font-semibold hover:bg-navy-800 transition-colors
                         focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
            >
              <DownloadIcon />
              Download all documents (ZIP)
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}
